/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Components
import {
    Box,
    Button,
    Container,
    Divider,
    Flex,
    Heading,
    Stack,
    useMultiStyleConfig,
    useTheme,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import LinksList from './links-list'

// Others
import {categoryUrlBuilder} from '../../utills/url'
import {parseCategoryColorConfig} from '../../utills/parseCategoryColorConfig'
import {MENU_DEFAULT_TEXT_COLOR, MENU_DEFAULT_HOVER_COLOR} from '../../constants'
// import Link from '@salesforce/retail-react-app/app/components/link'
import {pxToRem} from '../../utills/pxToRem'
import {ChevronLeftIcon, CloseIcon} from '../icons'
import {fonts} from '../../theme/foundations/fonts'

const ListMenuContentInner = ({
    item,
    onClose,
    items = [],
    initialFocusRef,
    selectedLevelOneMenu = {},
    setfeatureImage,
    setLinksForLevelThreeMenu,
    setLinksForLevelTwoMenu,
    setIsLevelThreeMenuOpen,
    setIsLevelTwoMenuOpen,
    isLevelTwoMenuOpen,
    isLevelThreeMenuOpen,
    isLevelTwo,
    isMobileMenu = false,
    setFeatureImageMobileLevelTwo,
    setFeatureImageMobileLevelThree,
    setFeatureImageMobileLevelFour,
    setMenuToggle,
    setSelectedLevelOneMenu,
    setSelectedLevelTwoMenu,
    selectedLevelTwoMenu
}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const {locale} = useIntl()
    const intl = useIntl()
    const variant = 'vertical'

    const styles = useMultiStyleConfig('LinksList', {variant})

    const [linksForContentComp, setLinksForContentComp] = useState([])

    useEffect(() => {
        if (
            !isMobileMenu &&
            (isLevelTwoMenuOpen || isLevelThreeMenuOpen) &&
            initialFocusRef?.current
        ) {
            initialFocusRef.current.focus()
        }
    }, [isLevelTwoMenuOpen, isLevelThreeMenuOpen, isMobileMenu, initialFocusRef])

    const handleCloseLevelThreeMenu = () => {
        setIsLevelThreeMenuOpen(false)
        setLinksForLevelThreeMenu([])
        setSelectedLevelTwoMenu({})
    }

    const handleCloseLevelTwoMenu = () => {
        setIsLevelTwoMenuOpen(false)
        setIsLevelThreeMenuOpen(false)
        setLinksForLevelTwoMenu([])
        setLinksForLevelThreeMenu([])
        setSelectedLevelOneMenu({})
        setSelectedLevelTwoMenu({})
    }

    const updateGTMdataLayer = (isLevel2, level1Text, level2Text) => {
        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'mainNavClick',
                valueL1: isLevel2 ? level1Text : level2Text,
                valueL2: `view all ${isLevel2 ? level1Text : level2Text}`
            })
        }
    }

    const currentBackCategory = isLevelTwo ? selectedLevelOneMenu : selectedLevelTwoMenu
    const parsedBackColors = parseCategoryColorConfig(currentBackCategory?.c_categoryTextColor)
    const backCategoryTextColor = parsedBackColors?.textColor ?? MENU_DEFAULT_TEXT_COLOR

    return (
        <Container
            as={Stack}
            {...baseStyle.popoverContainer}
            {...baseStyle.overflow}
            position={'relative'}
            marginTop={{base: pxToRem(50), sm: pxToRem(50), md: 0, lg: 0}}
        >
            <Flex
                direction="column"
                flex={'1'}
                borderRight={
                    (isLevelTwoMenuOpen || isLevelThreeMenuOpen) && !isMobileMenu
                        ? `${pxToRem(1)} solid ${MENU_DEFAULT_TEXT_COLOR}`
                        : `0 solid ${MENU_DEFAULT_TEXT_COLOR}`
                }
            >
                {/* close menu button */}
                {isMobileMenu && (isLevelTwoMenuOpen || isLevelThreeMenuOpen) && (
                    <Box
                        as="button"
                        type="button"
                        display="flex"
                        alignItems="center"
                        gap={pxToRem(8)}
                        paddingTop={pxToRem(10)}
                        paddingBottom={pxToRem(10)}
                        cursor="pointer"
                        onClick={isLevelTwo ? handleCloseLevelTwoMenu : handleCloseLevelThreeMenu}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                isLevelTwo ? handleCloseLevelTwoMenu() : handleCloseLevelThreeMenu()
                            }
                        }}
                        width="100%"
                        backgroundColor="transparent"
                        border="none"
                        padding={0}
                        textAlign="left"
                        aria-label={intl.formatMessage(
                            {
                                id: 'mobile_menu.button.back_to',
                                defaultMessage: 'Back to {category}'
                            },
                            {category: currentBackCategory?.text || 'previous menu'}
                        )}
                        _focus={{
                            outline: `${pxToRem(2)} solid ${MENU_DEFAULT_HOVER_COLOR}`,
                            outlineOffset: pxToRem(2)
                        }}
                        _focusVisible={{
                            outline: `${pxToRem(2)} solid ${MENU_DEFAULT_HOVER_COLOR}`,
                            outlineOffset: pxToRem(2)
                        }}
                    >
                        <ChevronLeftIcon
                            boxSize={pxToRem(14)}
                            color={MENU_DEFAULT_HOVER_COLOR}
                            sx={{
                                '& use': {
                                    transform: 'scale(1.5)',
                                    transformOrigin: 'center',
                                    stroke: 'currentColor',
                                    strokeWidth: '1'
                                }
                            }}
                        />
                        {currentBackCategory?.c_categoryIcon && (
                            <Box
                                as="span"
                                display="inline-flex"
                                alignItems="center"
                                dangerouslySetInnerHTML={{
                                    __html: currentBackCategory.c_categoryIcon
                                }}
                            />
                        )}
                        <Heading
                            fontFamily={fonts.primary}
                            fontSize={pxToRem(12)}
                            fontWeight="600"
                            letterSpacing={pxToRem(1)}
                            textTransform="uppercase"
                            color={backCategoryTextColor}
                            margin={0}
                        >
                            {currentBackCategory?.text}
                        </Heading>
                    </Box>
                )}

                {!isMobileMenu && (isLevelTwoMenuOpen || isLevelThreeMenuOpen) && (
                    <Button
                        ref={initialFocusRef}
                        onClick={isLevelTwo ? handleCloseLevelTwoMenu : handleCloseLevelThreeMenu}
                        aria-label={
                            isLevelTwo
                                ? `Close ${selectedLevelOneMenu?.text}`
                                : `Close ${selectedLevelTwoMenu?.text}`
                        }
                        _hover={{
                            textDecoration: 'none',
                            color: 'white'
                        }}
                        _active={{
                            backgroundColor: 'transparent'
                        }}
                        backgroundColor={'transparent'}
                        height={'auto'}
                        padding={'0'}
                        color={MENU_DEFAULT_TEXT_COLOR}
                        display={'block'}
                        width={'10px'}
                        fontSize={pxToRem(14)}
                        fontFamily={fonts.primary}
                        fontWeight={700}
                        justifyContent={'flex-end'}
                        position={'absolute'}
                        right={pxToRem(0)}
                    >
                        X
                    </Button>
                )}

                {(isLevelTwoMenuOpen || isLevelThreeMenuOpen) && (
                    <Link
                        href={
                            isLevelTwo
                                ? currentBackCategory?.c_alternativeUrl ||
                                  categoryUrlBuilder(selectedLevelOneMenu, locale)
                                : currentBackCategory?.c_alternativeUrl ||
                                  categoryUrlBuilder(selectedLevelTwoMenu, locale)
                        }
                        onClick={() => {
                            updateGTMdataLayer(
                                isLevelTwo,
                                selectedLevelOneMenu?.text,
                                selectedLevelTwoMenu?.text
                            )
                            if (setMenuToggle) setMenuToggle(false)
                            if (onClose) onClose()
                        }}
                        display="flex"
                        alignItems="center"
                        fontFamily={fonts.primary}
                        fontSize={pxToRem(14)}
                        fontWeight="500"
                        letterSpacing={pxToRem(1.12)}
                        lineHeight="normal"
                        fontStyle="normal"
                        color={backCategoryTextColor}
                        textDecoration="none"
                        _hover={{
                            textDecoration: 'underline',
                            color: MENU_DEFAULT_TEXT_COLOR
                        }}
                        _focus={{
                            outline: `${pxToRem(2)} solid ${MENU_DEFAULT_HOVER_COLOR}`,
                            outlineOffset: pxToRem(2),
                            textDecoration: 'underline',
                            color: MENU_DEFAULT_TEXT_COLOR
                        }}
                        _focusVisible={{
                            outline: `${pxToRem(2)} solid ${MENU_DEFAULT_HOVER_COLOR}`,
                            outlineOffset: pxToRem(2),
                            textDecoration: 'underline',
                            color: MENU_DEFAULT_TEXT_COLOR
                        }}
                    >
                        {isLevelTwo
                            ? `View All ${selectedLevelOneMenu?.text}`
                            : `View All ${selectedLevelTwoMenu?.text}`}
                    </Link>
                )}

                {items.map((menuItem) => {
                    const {id, name, c_headerMenuBanner, c_categoryTextColor, c_categoryIcon} =
                        menuItem

                    const heading = {
                        id: id,
                        image: c_headerMenuBanner,
                        href: categoryUrlBuilder(menuItem, locale),
                        text: name,
                        c_categoryTextColor,
                        c_categoryIcon,
                        styles: {
                            fontSize: 'md',
                            marginBottom: 2
                        }
                    }

                    const links = items
                        ? items.map((item) => {
                              const {name, id, categories} = item
                              return {
                                  id: id,
                                  categories: categories,
                                  name: name,
                                  href: categoryUrlBuilder(item, locale),
                                  text: name,
                                  styles: {
                                      fontSize: 'md',
                                      paddingTop: 3,
                                      paddingBottom: 3
                                  }
                              }
                          })
                        : []

                    return (
                        <>
                            <LinksList
                                key={id}
                                onLinkClick={onClose}
                                isInnerMenu={true}
                                heading={heading}
                                setSelectedLevelOneMenu={setSelectedLevelOneMenu}
                                selectedLevelTwoMenu={selectedLevelTwoMenu}
                                selectedLevelOneMenu={selectedLevelOneMenu}
                                setSelectedLevelTwoMenu={setSelectedLevelTwoMenu}
                                setMenuToggle={setMenuToggle}
                                hasSubMenu={menuItem.categories != null}
                                isMobileMenu={isMobileMenu}
                                links={links}
                                setfeatureImage={setfeatureImage}
                                color={'gray.900'}
                                setLinksForLevelThreeMenu={setLinksForLevelThreeMenu}
                                setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                setIsLevelThreeMenuOpen={setIsLevelThreeMenuOpen}
                                setLinksForContentComp={setLinksForContentComp}
                                setFeatureImageMobileLevelTwo={setFeatureImageMobileLevelTwo}
                                setFeatureImageMobileLevelThree={setFeatureImageMobileLevelThree}
                                setFeatureImageMobileLevelFour={setFeatureImageMobileLevelFour}
                                parentCategoryName={
                                    isLevelTwo
                                        ? selectedLevelOneMenu?.text
                                        : selectedLevelTwoMenu?.text
                                }
                            />
                        </>
                    )
                })}
            </Flex>
        </Container>
    )
}

ListMenuContentInner.propTypes = {
    item: PropTypes.object,
    items: PropTypes.array,
    maxColumns: PropTypes.number,
    setSelectedLevelOneMenu: PropTypes.func,
    setSelectedLevelTwoMenu: PropTypes.func,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object,
    selectedLevelOneMenu: PropTypes.object,
    selectedLevelTwoMenu: PropTypes.object,
    setLinksForLevelThreeMenu: PropTypes.func,
    setLinksForLevelTwoMenu: PropTypes.func,
    setIsLevelThreeMenuOpen: PropTypes.func,
    setIsLevelTwoMenuOpen: PropTypes.func,
    isLevelTwoMenuOpen: PropTypes.oneOf[(true, false)],
    isLevelThreeMenuOpen: PropTypes.oneOf[(true, false)],
    isLevelTwo: PropTypes.oneOf[(true, false)],
    isMobileMenu: PropTypes.oneOf[(true, false)],
    setFeatureImageMobileLevelTwo: PropTypes.func,
    setFeatureImageMobileLevelThree: PropTypes.func,
    setFeatureImageMobileLevelFour: PropTypes.func,

    setMenuToggle: PropTypes.func,
    setfeatureImage: PropTypes.func
}

export {ListMenuContentInner}
